export interface SubscriptionManager {
  registerSubscription: (unsubscribe: () => void, category?: string, key?: string) => void
  unregisterSubscription: (unsubscribe: () => void) => void
  clearAllSubscriptions: () => void
  clearSubscriptionsByCategory: (category: string) => void
  getActiveSubscriptionCount: (category?: string) => number
  getAllSubscriptionCategories: () => string[]
  getSubscriptionByKey: (key: string) => (() => void) | undefined
  hasSubscriptionWithKey: (key: string) => boolean
  clearSubscriptionByKey: (key: string) => boolean
  getActiveSubscriptions: () => Array<{
    unsubscribe: () => void
    category: string
    key?: string
    createdAt: Date
  }>
}

interface SubscriptionMeta {
  category: string
  key?: string
  createdAt: Date
}

/**
 * Global subscription tracking composable
 *
 * Provides centralized management of Firestore subscriptions throughout the app.
 * - Tracks subscriptions by category and key
 * - Ensures proper cleanup on logout or component unmount
 * - Prevents memory leaks from unmanaged subscriptions
 * - Prevents duplicate subscriptions with the same key
 */
export function useSubscriptions(): SubscriptionManager {
  // Use a global state for tracking subscriptions across components
  const subscriptions = useState<Map<() => void, SubscriptionMeta>>(
    'global-subscriptions', () => new Map(),
  )

  // Map to access subscriptions by key for deduplication
  const subscriptionsByKey = useState<Map<string, () => void>>(
    'subscriptions-by-key', () => new Map(),
  )

  /**
   * Register a subscription for tracking
   * @param unsubscribe The unsubscribe function returned by onSnapshot
   * @param category Optional category for grouping subscriptions
   * @param key Optional unique key to prevent duplicates
   */
  const registerSubscription = (unsubscribe: () => void, category = 'default', key?: string) => {
    // If a key is provided, check for an existing subscription with that key
    if (key) {
      const existingUnsubscribe = subscriptionsByKey.value.get(key)

      if (existingUnsubscribe) {
        // Unsubscribe from the existing subscription to prevent duplicates
        try {
          existingUnsubscribe()
          console.warn(`[Subscriptions] Replacing existing subscription with key: ${key}`)
          // Remove from our tracking
          subscriptions.value.delete(existingUnsubscribe)
          subscriptionsByKey.value.delete(key)
        }
        catch (error) {
          console.error('[Subscriptions] Error unsubscribing from existing subscription:', error)
        }
      }

      // Store in key map for future lookups
      subscriptionsByKey.value.set(key, unsubscribe)
    }

    // Store in main map with metadata
    subscriptions.value.set(unsubscribe, {
      category,
      key,
      createdAt: new Date(),
    })

    // For development debugging
    if (import.meta.dev) {
      console.warn(`[Subscriptions] Registered (${category})${key ? ` with key "${key}"` : ''}: Active=${subscriptions.value.size}`)
    }
  }

  /**
   * Unregister a specific subscription
   * @param unsubscribe The unsubscribe function to remove
   */
  const unregisterSubscription = (unsubscribe: () => void) => {
    // Get the metadata before removing
    const meta = subscriptions.value.get(unsubscribe)

    // Call the unsubscribe function first
    try {
      unsubscribe()
    }
    catch (error) {
      console.error('[Subscriptions] Error unsubscribing:', error)
    }

    // Remove from key map if it has a key
    if (meta?.key) {
      subscriptionsByKey.value.delete(meta.key)
    }

    // Then remove from our tracking
    subscriptions.value.delete(unsubscribe)

    // For development debugging
    if (import.meta.dev && meta) {
      console.warn(`[Subscriptions] Unregistered (${meta.category})${meta?.key ? ` with key "${meta.key}"` : ''}: Active=${subscriptions.value.size}`)
    }
  }

  /**
   * Clear all active subscriptions
   */
  const clearAllSubscriptions = () => {
    // Call each unsubscribe function
    subscriptions.value.forEach((meta, unsubscribe) => {
      try {
        unsubscribe()
      }
      catch (error) {
        console.error(`[Subscriptions] Error clearing subscription (${meta.category}):`, error)
      }
    })

    // Clear the tracking maps
    subscriptions.value.clear()
    subscriptionsByKey.value.clear()

    // For development debugging
    if (import.meta.dev) {
      console.warn('[Subscriptions] Cleared all subscriptions')
    }
  }

  /**
   * Clear all subscriptions for a specific category
   * @param category The category to clear
   */
  const clearSubscriptionsByCategory = (category: string) => {
    // First collect all matching unsubscribe functions
    const toRemove: (() => void)[] = []
    subscriptions.value.forEach((meta, unsubscribe) => {
      if (meta.category === category) {
        toRemove.push(unsubscribe)
      }
    })

    if (import.meta.dev) {
      console.warn(`[Subscriptions] Clearing category "${category}" (${toRemove.length} subscriptions)`)
    }

    // Then unsubscribe and remove each one
    toRemove.forEach((unsubscribe) => {
      try {
        const meta = subscriptions.value.get(unsubscribe)
        unsubscribe()

        // Remove from key map if it has a key
        if (meta?.key) {
          subscriptionsByKey.value.delete(meta.key)
        }

        subscriptions.value.delete(unsubscribe)
      }
      catch (error) {
        console.error(`[Subscriptions] Error clearing subscription (${category}):`, error)
      }
    })
  }

  /**
   * Get a subscription by its unique key
   * @param key The unique key to look for
   * @returns The unsubscribe function if found
   */
  const getSubscriptionByKey = (key: string): (() => void) | undefined => {
    return subscriptionsByKey.value.get(key)
  }

  /**
   * Check if a subscription with the given key exists
   * @param key The unique key to check
   * @returns True if subscription exists
   */
  const hasSubscriptionWithKey = (key: string): boolean => {
    return subscriptionsByKey.value.has(key)
  }

  /**
   * Clear a subscription by its unique key
   * @param key The unique key to clear
   * @returns True if found and cleared
   */
  const clearSubscriptionByKey = (key: string): boolean => {
    const unsubscribe = subscriptionsByKey.value.get(key)

    if (unsubscribe) {
      unregisterSubscription(unsubscribe)
      return true
    }

    return false
  }

  /**
   * Get count of active subscriptions
   * @param category Optional category to filter by
   * @returns Number of active subscriptions
   */
  const getActiveSubscriptionCount = (category?: string): number => {
    if (!category) {
      return subscriptions.value.size
    }

    let count = 0
    subscriptions.value.forEach((meta) => {
      if (meta.category === category) count++
    })
    return count
  }

  /**
   * Get all unique subscription categories
   * @returns Array of unique category names
   */
  const getAllSubscriptionCategories = (): string[] => {
    const categories = new Set<string>()
    subscriptions.value.forEach((meta) => {
      categories.add(meta.category)
    })
    return Array.from(categories)
  }

  /**
   * Get detailed information about all active subscriptions
   * @returns Array of subscription detail objects
   */
  const getActiveSubscriptions = (): Array<{
    unsubscribe: () => void
    category: string
    key?: string
    createdAt: Date
  }> => {
    // Cast entries for proper typing
    const entries = Array.from(subscriptions.value.entries()) as Array<[() => void, SubscriptionMeta]>
    return entries.map(([unsubscribe, meta]) => ({
      unsubscribe,
      category: meta.category,
      key: meta.key,
      createdAt: meta.createdAt,
    }))
  }

  return {
    registerSubscription,
    unregisterSubscription,
    clearAllSubscriptions,
    clearSubscriptionsByCategory,
    getActiveSubscriptionCount,
    getAllSubscriptionCategories,
    getSubscriptionByKey,
    hasSubscriptionWithKey,
    clearSubscriptionByKey,
    getActiveSubscriptions, // Add the new method
  }
}
